import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Star,
  Users,
  MapPin,
  Calendar,
  TrendingUp,
  Eye,
  MoreVertical,
  Heart,
  ArrowRight,
  Briefcase,
  Plus
} from 'lucide-react'
import { Link } from 'react-router-dom'

interface FavoritePosition {
  id: string
  title: string
  status: 'active' | 'paused' | 'filled' | 'open' | 'draft'
  applicationCount: number
  createdAt: string
  department?: string
  location?: string
}

interface FavoritePositionsProps {
  positions: FavoritePosition[]
  loading?: boolean
  onToggleFavorite?: (positionId: string) => void
}

export default function FavoritePositions({ 
  positions = [], 
  loading = false,
  onToggleFavorite 
}: FavoritePositionsProps) {
  const [hoveredPosition, setHoveredPosition] = useState<string | null>(null)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'open':
        return 'bg-green-100 text-green-800'
      case 'paused':
        return 'bg-yellow-100 text-yellow-800'
      case 'filled':
        return 'bg-blue-100 text-blue-800'
      case 'draft':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 3600 * 24))

    if (diffInDays === 0) {
      return 'Today'
    } else if (diffInDays === 1) {
      return 'Yesterday'
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Star className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900">Your Top Positions</h3>
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-300 rounded-lg"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-300 rounded w-32"></div>
                    <div className="h-3 bg-gray-300 rounded w-20"></div>
                  </div>
                </div>
                <div className="h-6 bg-gray-300 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Star className="w-5 h-5 text-yellow-500 fill-current" />
          <h3 className="text-lg font-semibold text-gray-900">Your Top Positions</h3>
        </div>
        <Link
          to="/recruiter/positions"
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          View All
        </Link>
      </div>

      {positions.length === 0 ? (
        <div className="text-center py-8">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-sm font-medium text-gray-900 mb-2">No Positions Yet</h4>
          <p className="text-sm text-gray-600 mb-4">
            You don't have any favorite positions yet. Visit the positions page to view all available positions.
          </p>
          <Link
            to="/recruiter/positions"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Briefcase className="w-4 h-4 mr-2" />
            View Positions
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {positions.map((position, index) => (
            <motion.div
              key={position.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onMouseEnter={() => setHoveredPosition(position.id)}
              onMouseLeave={() => setHoveredPosition(null)}
              className="group relative border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer"
            >
              <Link to={`/recruiter/positions/${position.id}`} className="block">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-blue-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {position.title}
                        </h4>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(position.status)}`}>
                          {position.status}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Users className="w-3 h-3" />
                          <span>{position.applicationCount} applications</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>Posted {formatDate(position.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {onToggleFavorite && (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          onToggleFavorite(position.id)
                        }}
                        className="p-1 text-yellow-500 hover:text-yellow-600 transition-colors"
                        title="Remove from favorites"
                      >
                        <Heart className="w-4 h-4 fill-current" />
                      </button>
                    )}
                    
                    <Eye className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                </div>

                {/* Performance Indicator */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium text-gray-900">
                        {position.applicationCount > 10 ? 'High' : position.applicationCount > 5 ? 'Medium' : 'Low'}
                      </span> application volume
                    </div>
                    
                    <div className="flex items-center space-x-1 text-xs">
                      {position.applicationCount > 0 ? (
                        <TrendingUp className={`w-3 h-3 ${position.applicationCount > 10 ? 'text-green-500' : position.applicationCount > 5 ? 'text-yellow-500' : 'text-gray-400'}`} />
                      ) : (
                        <div className="w-3 h-3" />
                      )}
                      <ArrowRight className="w-3 h-3 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      {positions.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>Showing top {positions.length} performing positions</span>
            <Link
              to="/recruiter/positions/analytics"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              View Analytics
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}